import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { first } from 'lodash';

import IMG_SLICK from 'assets/images/logos/slick-logo-text.png';
import validateEmail from 'bl/validation/validateEmail.bl';
import validatePassword from 'bl/validation/validatePassword.bl';
import ReloadChecker from 'components/core/ReloadChecker.component';
import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import CONF from 'config/conf.singleton';
import SEGMENT from 'config/segment.singleton';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import asArray from 'utils/arrays/asArray.util';
import {
  clearCognitoUserTokens,
  COGNITO_ATTRIBUTE,
  COGNITO_STATE,
  selectCognitoState,
  selectCognitoUser,
  withRcmLogin,
} from 'utils/cognito.util';
import fromObjectToQueryString from 'utils/converters/object/fromObjectToQueryString.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import determineFbSalonId from 'utils/determineFbSalonId.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import parseJson from 'utils/parsers/parseJson';
import randomString from 'utils/values/randomString.util';

import { normalizeEmail } from 'lib/normalize';
import InputField from 'onboarding/common/InputField';
import { setRedirectUri } from 'salon/actions';
import SlickSlider from 'user/common/SlickSlider';

import CN from './Login.module.scss';

const CTAG = 'Login';
const L = logger(CTAG);

const LINK_MAIL = 'mailto:support@getslick.com';
const LINK_PHONE = 'tel:+441202286005';
const LINK_POLICY = 'https://www.getslick.com/legal/privacy-policy';

const PW_ID_USER = 'UserEmail';
const PW_ID_PASS = 'PasswordInput';

const extractCognitoInfo = (attributes) => {
  const salonsListManaged = asArray(parseJson(attributes?.[COGNITO_ATTRIBUTE.salonsManage]));
  const salonsListAccess = asArray(parseJson(attributes?.[COGNITO_ATTRIBUTE.salonsAccess]));

  return {
    isOnboardingCompleted: '1' === attributes?.[COGNITO_ATTRIBUTE.onboardingCompleted],
    salonsListManaged,
    salonsListAccess,
    salonsCount: salonsListManaged.length + salonsListAccess.length,
  };
};

const fromErrorToMessage = (error) => {
  const { code: c, message: m } = error ?? {};

  if (c === 'PasswordResetRequiredException') return 'Password Reset Required';
  if (c === 'UserNotFoundException' && m === 'User does not exist.') return 'This user does not exist.';
  if (c === 'NotAuthorizedException' && m === 'User is disabled.') {
    return 'User is disabled. Please contact customer support';
  }

  return <div className={CN.error}>Your username or password is incorrect</div>;
};

const goToNextStep = ({ prevProps, nextProps: props, redirectUri }) => {
  const FTAG = 'goToNextStep()';

  const salonIdFromFacebook = window.faceBookSalonId;
  const salonIdFromPrevProps = prevProps?.salonId ?? null;
  const salonIdFromNextProps = props.salonId ?? null;
  const username = props.user?.username;

  const attributes = props?.attributes;

  L.info(FTAG, { username, salonIdFromPrevProps, salonIdFromNextProps, salonIdFromFacebook });

  const { isOnboardingCompleted, salonsListManaged, salonsListAccess, salonsCount } = extractCognitoInfo(attributes);

  if (username) SEGMENT.identify({ id: username });

  if (salonIdFromNextProps || salonIdFromFacebook) {
    const info = { salonIdFromNextProps, salonIdFromFacebook };
    L.info(FTAG, info, 'redirecting to online setting...');

    const state = { fromFB: true };
    return void void navFor(CTAG, FTAG, info, props).push({
      pathname: linkTo({
        pattern: LINK.salonOnline,
        params: { salonId: salonIdFromNextProps || salonIdFromFacebook },
      }),
      state,
      search: fromObjectToQueryString({ state }),
    });
  }

  if (!attributes || !isOnboardingCompleted || props.cognitoState === COGNITO_STATE.needConfirmation) {
    const info = { cognitoState: props.cognitoState, attributes, isOnboardingCompleted };
    L.info(FTAG, info, 'redirecting to onboarding...');
    return void navFor(CTAG, FTAG, info, props).push(LINK.onboarding);
  }

  if (salonsCount > 1) {
    const info = { salonsCount };
    L.info(FTAG, info, 'redirecting to salon list...');
    return void navFor(CTAG, FTAG, info, props).push(LINK.salonPick);
  }

  if (salonsCount < 1) {
    const info = { salonsCount };
    L.info(FTAG, info, 'redirecting to no access landing page...');
    return void navFor(CTAG, FTAG, info, props).push(LINK.noAccess);
  }

  const salonId = first(salonsListManaged.length ? salonsListManaged : salonsListAccess);
  SEGMENT.setCompany({ id: salonId });

  if (redirectUri) {
    const info = { redirectUri };
    L.info(FTAG, info, 'redirecting to salon landing page...');
    return void navFor(CTAG, FTAG, info, props).push(linkTo({ pattern: LINK.salonLanding, params: { salonId } }));
  }

  L.info(FTAG, 'redirecting to salon today page...');
  return void navFor(CTAG, FTAG, props).push(linkTo({ pattern: LINK.salonToday, params: { salonId } }));
};

class LoginForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      username: '',
      password: '',
      canLogin: false,
      hidePasswordText: true,
      errorMessage: undefined,
      passError: false,
      type: 'password',
      redirectUri: '',
      emailError: false,
    };
    this.passwordElementRef = React.createRef();
    this.emailElementRef = React.createRef();
  }

  componentDidMount() {
    determineFbSalonId();
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextState) {
    const { error, cognitoState } = nextProps;
    const { password } = nextState;
    const { redirectUri: redirectUriFromState } = this.state;
    const { setRedirectUri } = this.props;

    // http://localhost:3000/?redirect_uri=https%3A%2F%2Fwww.facebook.com%2Flocal%2Fdev%2Ftransaction_tool_selector%2Fredirect%3Fapp_id%3D2815615545366839%26app_name%3DSlick%26page_id%3D1595465354060456
    const redirectUri = unescape(window.location.href).split('redirect_uri=')[1];
    const urIregex = /(facebook|instagram|whatsapp)\.com(\/.*)?/;

    if (redirectUri && !redirectUriFromState) {
      const redirectFromOutside = urIregex.test(redirectUri);
      L.info('componentWillReceiveProps()', { redirectUri, redirectFromOutside });

      this.setState({ redirectUri });

      // only set it when it's from facebook or instagram or whatsapp
      if (redirectFromOutside) setRedirectUri(redirectUri.split('#')[0]); // remove #/salon appended by react to the redirect URI
    }

    if (cognitoState === COGNITO_STATE.in || cognitoState === COGNITO_STATE.needConfirmation) {
      return void this.setState(
        { errorMessage: undefined },
        () => void goToNextStep({ redirectUri, nextProps, prevProps: this.props }),
      );
    }

    if (cognitoState === COGNITO_STATE.needNewPassword) {
      L.info('componentWillReceiveProps()', 'redirecting to password reset...');
      return void navFor(CTAG, 'componentWillReceiveProps()', nextProps).push(LINK.loginForceReset);
    }

    if (cognitoState === COGNITO_STATE.out) {
      if (asObject(error).code !== '') this.setState({ errorMessage: fromErrorToMessage(error) });

      // for saved password
      if (password !== '') this.checkButton(password);
    }

    return void L.info('componentWillReceiveProps()', 'Unhandled cognitoState:', cognitoState);
  }

  onChange = (event) => {
    const password = event.currentTarget.value;
    this.setState({ password });

    const errorMessage = this.validatePassword(password);
    this.setState({ errorMessage });
  };

  onBlur = (event) => {
    const password = event.currentTarget.value;

    const errorMessage = this.validatePassword(password);
    this.setState({ errorMessage });

    this.changePassword(password);
  };

  onKeyUp = (event) => void (event.key.toLowerCase() === 'enter' && this.handleClick());
  changePassword = (password) => void this.setState({ password });
  changeUsername = (value) => void this.setState({ username: normalizeEmail(value) });

  handleClick = () => {
    const FTAG = 'handleClick()';

    const { password, username } = this.state;
    const { onSubmit, cognitoUser, cognitoState } = this.props;

    if (COGNITO_STATE.in === cognitoState) {
      return void goToNextStep({ nextProps: this.props });
    }

    const clearCognitoUserTokensResult = clearCognitoUserTokens({ cognitoUser });
    L.debug(FTAG, 'clearCognitoUserTokensResult:', clearCognitoUserTokensResult);

    const promise = onSubmit(username, password);
    L.unignore(FTAG, 'onSubmit:', promise).then((onSubmitResult) => L.debug(FTAG, 'onSubmitResult:', onSubmitResult));
  };

  validateEmail = (value) => {
    if (validateEmail({ value })) {
      this.setState({ emailError: false }, () => void this.checkButton());
      return '';
    }

    this.setState({ emailError: true }, () => void this.checkButton());
    return <span className={CN.error}>Please enter a valid email</span>;
  };

  validatePassword = (value) => {
    if (validatePassword({ value })) {
      this.setState({ passError: false }, () => void this.checkButton());
      return '';
    }

    this.setState({ passError: true }, () => void this.checkButton());

    return (
      <span className={CN.error}>
        Password needs to be at least 8 characters in length and contain an uppercase character and a number.
      </span>
    );
  };

  checkButton = (inputPassword) => {
    const { password, errorMessage, username } = this.state;
    this.setState({ canLogin: username !== '' && (inputPassword || password) !== '' && !errorMessage });
  };

  showHidePassword = () => void this.setState({ type: this.state.type === 'text' ? 'password' : 'text' });

  render() {
    const { cognitoState } = this.props;
    const { canLogin, password, type, errorMessage, passError, emailError } = this.state;

    const isSpinning =
      cognitoState === COGNITO_STATE.in ||
      cognitoState === COGNITO_STATE.loading ||
      cognitoState === COGNITO_STATE.authenticated;

    const idUser = PW_ID_USER + randomString();
    const idPass = PW_ID_PASS + randomString();

    const isDevOrAlpha = CONF.environment === 'development' || window.location.href.indexOf('alpha') > 0;

    return (
      <div data-bem="LoginForm" className={CN.component}>
        <ReloadChecker force tag={CTAG} />

        <div className={CN.layout}>
          <div className={CN.loginColumn}>
            <div className={CN.loginPanel}>
              <img src={IMG_SLICK} alt="Slick logo" className={CN.logo} />
              <div className={CN.title}>
                Hey, <span className={CN.bold}>welcome back!</span> {S.wavingHand}
              </div>
              <div
                className={cn({
                  [CN.inputContainer]: true,
                  [CN.hasError]: emailError,
                })}
              >
                <label htmlFor={idUser} className={CN.inputLabel}>
                  Email address
                </label>

                <InputField
                  data={{
                    id: idUser,
                    type: 'text',
                    placeholder: 'you@example.com',
                    inputValue: '',
                  }}
                  pwId={PW_ID_USER}
                  ref={this.emailElementRef}
                  validateBlur={this.validateEmail}
                  onBlurHandler={this.changeUsername}
                  className={cn({
                    [CN.input]: true,
                    [CN.hasError]: emailError,
                  })}
                />
              </div>

              <div className={CN.inputContainer}>
                <label htmlFor={idPass} className={CN.inputLabel}>
                  Password
                </label>
                <div className={CN.passwordContainer}>
                  <input
                    data-input-type="password"
                    data-pw-id={PW_ID_PASS}
                    id={idPass}
                    type={type}
                    placeholder="●●●●●●●●"
                    value={password}
                    onChange={this.onChange}
                    ref={this.passwordElementRef}
                    onKeyUp={this.onKeyUp}
                    className={cn({
                      [CN.input]: true,
                      [CN.hasError]: passError,
                    })}
                  />

                  <div className={CN.eyeContainer} role="button" tabIndex="0" onClick={this.showHidePassword}>
                    {this.state.type === 'password' ? <div className={CN.eyeOpen} /> : <div className={CN.eyeClose} />}
                  </div>
                </div>
              </div>
              {errorMessage && <span className={CN.errorContainer}>{errorMessage}</span>}
              <div className={CN.forgotPassContainer}>
                <Link to={LINK.loginForgot} href={LINK.loginForgot} className={CN.link}>
                  Forgot password?
                </Link>
              </div>
              <div className={CN.buttonContainer}>
                <Button
                  tag="LoginForm__login"
                  pwId="login"
                  variant="primaryForce"
                  width="max"
                  height="max"
                  onClick={this.handleClick}
                  disabled={!canLogin || isSpinning}
                >
                  <span className={CN.buttonFont}>LOGIN</span>
                </Button>
              </div>

              <div className={CN.signInContainer}>
                {isDevOrAlpha ? (
                  <div>
                    Don't have an account?
                    <Link to={LINK.onboarding} href={LINK.onboarding} className={CN.link}>
                      SIGN UP
                    </Link>
                  </div>
                ) : (
                  <div className={CN.signUpLink}>
                    Don't have an account?
                    <ExternalLink link="https://getslick.com/demo">
                      <div className={CN.link}> SIGN UP</div>
                    </ExternalLink>
                  </div>
                )}
              </div>
              <div className={CN.supportContainer}>
                <div className={CN.space}>Can{S.apos}t log in?</div>
                <div className={CN.space}>Reach out via the live chat bubble</div>
                <div className={CN.loginLabel}>
                  Email:
                  <ExternalLink link={LINK_MAIL} variant="inline">
                    <span className={CN.linkTo}>support@getslick.com</span>
                  </ExternalLink>
                </div>
                <div className={CN.loginLabel}>
                  Phone:
                  <ExternalLink link={LINK_PHONE} variant="inline">
                    <span className={CN.linkTo}>01202 286005</span>
                  </ExternalLink>
                </div>
              </div>
              <div className={CN.policyContainer}>
                <ExternalLink link={LINK_POLICY} variant="inline">
                  <span className={CN.linkToPolicy}>PRIVACY POLICY</span>
                </ExternalLink>
              </div>
            </div>
          </div>

          <div className={CN.sliderColumn}>
            <SlickSlider />
          </div>
        </div>
      </div>
    );
  }
}

const LoginFormHoC = withRouter(
  connect(
    (state) => ({
      cognitoUser: selectCognitoUser(state),
      cognitoState: selectCognitoState(state),
      user: state?.cognito?.user,
      attributes: state?.cognito?.attributes,
      error: state?.cognito?.error,
    }),
    mapActionsToDispatchToProps({
      setRedirectUri,
    }),
  )(LoginForm),
);

const LoginHoc = withRcmLogin(LoginFormHoC);

// this extra export is needed for tests that provide their own mock for connect()
export { LoginForm as LoginForm4Test };
export default LoginHoc;
